/**
 * MinRoughnessClamp — Prevents specular firefly artifacts on near-mirror surfaces.
 *
 * Automotive GLBs often contain chrome trim, black gloss panels, and door-seal
 * rubbers with roughness values of 0.0–0.02.  At such low roughness the IBL
 * specular lobe becomes sub-pixel narrow.  Combined with high dynamic range HDRIs
 * (bright sky, golden-hour sun) this causes individual fragments to sample a
 * bright cubemap texel while neighbours sample near-black → white "star" dots
 * that twinkle when rotating, especially visible on dark scenes.
 *
 * Fix: clamp `roughnessFactor` in the fragment shader to a minimum value so the
 * lobe is always at least 1–2 pixels wide. Value of 0.04 is perceptually
 * invisible on polished clear-coat but eliminates the worst IBL fireflies.
 *
 * Cost: 1 `max()` operation per fragment — essentially free.
 *
 * Compatible with MeshStandardMaterial and MeshPhysicalMaterial.
 * Safe to stack with DownFacingAO (uses `extendMaterialShader` key tracking).
 *
 * @example
 *   applyMinRoughnessClamp(mat);              // default clamp 0.04
 *   applyMinRoughnessClamp(mat, { min: 0.07 }); // stronger suppression
 *
 * @see packages/engine/src/materials/DownFacingAO.ts — sister shader effect
 */

import {
  Mesh,
  MeshStandardMaterial,
  MeshPhysicalMaterial,
  type Material,
  type Object3D,
} from 'three';
import { extendMaterialShader } from './extendMaterialShader';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/** Options for the min-roughness clamp effect. */
export interface MinRoughnessClampOptions {
  /**
   * Minimum roughness value clamped in the fragment shader.
   * Lower values = more specular allowed, higher = more suppression.
   * @default 0.04
   */
  min?: number;
}

const MOD_KEY = 'minRough:v1';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Patch a material so `roughnessFactor` is clamped to a minimum value
 * before IBL specular is computed.
 *
 * Idempotent: calling twice on the same material is a no-op.
 *
 * @param mat  Three.js material to patch.
 * @param opts Optional min roughness override.
 */
export function applyMinRoughnessClamp(
  mat: Material,
  opts: MinRoughnessClampOptions = {},
): void {
  // Only works on materials that use Three.js's standard PBR lighting model
  if (!(mat instanceof MeshStandardMaterial) && !(mat instanceof MeshPhysicalMaterial)) {
    return;
  }

  // Glass/transmissive materials need their own roughness untouched
  // (roughness controls transmission blur, not specular appearance here).
  if (mat instanceof MeshPhysicalMaterial && mat.transmission > 0) {
    return;
  }

  const minRoughness = opts.min ?? 0.04;

  extendMaterialShader(mat, MOD_KEY, (shader) => {
    // Inject after roughnessmap_fragment which sets `roughnessFactor`.
    // The clamp ensures the IBL specular lobe never collapses to sub-pixel size.
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <roughnessmap_fragment>',
      /* glsl */ `
        #include <roughnessmap_fragment>
        roughnessFactor = max(roughnessFactor, ${minRoughness.toFixed(4)});
      `,
    );
  });
}

// ---------------------------------------------------------------------------
// Scene-level helper
// ---------------------------------------------------------------------------

/**
 * Traverse an entire scene graph and apply the min-roughness clamp to every
 * MeshStandardMaterial / MeshPhysicalMaterial found on any mesh.
 *
 * Idempotent per-material — safe to call after MaterialManager.init().
 *
 * @param root The root Object3D to traverse (typically the cloned GLB scene).
 * @param opts Min roughness override.
 */
export function applySceneMinRoughnessClamp(
  root: Object3D,
  opts: MinRoughnessClampOptions = {},
): void {
  root.traverse((child) => {
    if (!(child instanceof Mesh)) return;
    const mats: Material[] = Array.isArray(child.material)
      ? child.material
      : child.material
        ? [child.material]
        : [];
    for (const mat of mats) {
      applyMinRoughnessClamp(mat, opts);
    }
  });
}
